import React, { useState, useEffect } from 'react';
import { StationType, getConfig } from '../Firebase/firebaseApi.ts';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Box, Typography, IconButton, ToggleButton, ToggleButtonGroup } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { he } from 'date-fns/locale';
import RecurringSeriesPicker from './RecurringSeriesPicker.tsx';

const MIN_MONTH_COUNT = 1;
const MAX_MONTH_COUNT = 11;

interface DateTimePickerProps {
  selectedType: StationType;
  selectedSlotType: string; // The currently selected slot type
  selectedDate: string;
  selectedEndDate: string;
  selectedStartTime: string;
  selectedEndTime: string;
  monthCount: number;
  dateMode: 'single' | 'recurring';
  onDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onStartTimeChange: (time: string) => void;
  onEndTimeChange: (time: string) => void;
  onMonthCountChange: (n: number) => void;
  onDateModeChange: (mode: 'single' | 'recurring') => void;
  onSeriesDatesChange: (dates: string[]) => void;
}

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  selectedType,
  selectedSlotType,
  selectedDate,
  selectedEndDate,
  selectedStartTime,
  selectedEndTime,
  monthCount,
  dateMode,
  onDateChange,
  onEndDateChange,
  onStartTimeChange,
  onEndTimeChange,
  onMonthCountChange,
  onDateModeChange,
  onSeriesDatesChange,
}) => {
  const isHour = selectedSlotType === 'hour';
  const isDayOrMonth = selectedSlotType === 'day' || selectedSlotType === 'month';
  const isMonth = selectedSlotType === 'month';
  const canUseRecurring = selectedSlotType === 'hour' || selectedSlotType === 'day';

  const [businessHours, setBusinessHours] = useState({ 
    startHour: 8, 
    startMinute: 30, 
    endHour: 20, 
    endMinute: 30 
  });

  useEffect(() => {
    if (!canUseRecurring && dateMode === 'recurring') {
      onDateModeChange('single');
    }
  }, [canUseRecurring, dateMode, onDateModeChange]);

  useEffect(() => {
    const loadConfig = async () => {
      const config = await getConfig();
      if (config?.booking) {
        // Parse start time (e.g., "08:30")
        const [startHourStr, startMinuteStr] = config.booking.startHour.split(':');
        const startHour = parseInt(startHourStr);
        const startMinute = parseInt(startMinuteStr);
        
        // Parse end time (e.g., "20:30")
        const [endHourStr, endMinuteStr] = config.booking.endHour.split(':');
        const endHour = parseInt(endHourStr);
        const endMinute = parseInt(endMinuteStr);
        
        setBusinessHours({
          startHour,
          startMinute,
          endHour,
          endMinute
        });
      }
    };
    loadConfig();
  }, []);

  // Helper functions to convert between string and Date
  const stringToDate = (dateString: string): Date | null => {
    if (!dateString) return null;
    // Parse date string as local date to avoid timezone issues
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed in Date constructor
  };

  const dateToString = (date: Date | null): string => {
    if (!date) return '';
    // Use local date methods to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const stringToTime = (timeString: string): Date | null => {
    if (!timeString) return null;
    const today = new Date();
    const [hours, minutes] = timeString.split(':');
    today.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    return today;
  };

  const timeToString = (date: Date | null): string => {
    if (!date) return '';
    return date.toTimeString().slice(0, 5);
  };

  // Minimum date allowed: January 13, 2026
  const minAllowedDate = new Date(2026, 0, 13); // month is 0-indexed (0 = January)
  minAllowedDate.setHours(0, 0, 0, 0);
  
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set to start of today for date comparison
  
  // Use the later of today or minAllowedDate as the minimum date
  const minDate = today > minAllowedDate ? today : minAllowedDate;
  
  const now = new Date();
  const nowRounded = new Date(now);
  // Round current time to the nearest 30-minute interval
  const currentMinutes = nowRounded.getMinutes();
  const roundedMinutes = Math.ceil(currentMinutes / 30) * 30; // Round up to next 30-minute interval
  nowRounded.setMinutes(roundedMinutes, 0, 0);
  const isToday = selectedDate === dateToString(new Date());

  // Disable weekends (Friday = 5, Saturday = 6).
  // Sunday: blocked for meeting-room day/month (full day includes morning);
  // hour bookings are allowed from 14:00 via minTime.
  const shouldDisableDate = (date: Date): boolean => {
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 5 || dayOfWeek === 6) return true;
    if (
      selectedType?.type === 'meetingRoom' &&
      dayOfWeek === 0 &&
      (selectedSlotType === 'day' || selectedSlotType === 'month')
    ) {
      return true;
    }
    return false;
  };

  // Business hours from config
  const businessHoursStart = new Date();
  businessHoursStart.setHours(businessHours.startHour, businessHours.startMinute, 0, 0);

  const businessHoursEnd = new Date();
  businessHoursEnd.setHours(businessHours.endHour, businessHours.endMinute, 0, 0);

  // Meeting rooms on Sunday: bookings only from 14:00
  const sundayMeetingRoomMinTime = (() => {
    if (selectedType?.type !== 'meetingRoom' || !selectedDate) return null;
    const date = stringToDate(selectedDate);
    if (!date || date.getDay() !== 0) return null;
    const min = new Date();
    min.setHours(14, 0, 0, 0);
    return min;
  })();

  const getMinStartTime = (): Date => {
    const minStart = isToday && nowRounded > businessHoursStart ? nowRounded : businessHoursStart;
    if (sundayMeetingRoomMinTime && sundayMeetingRoomMinTime > minStart) {
      return sundayMeetingRoomMinTime;
    }
    return minStart;
  };


  const recurringDisabledWeekdays =
    selectedType?.type === 'meetingRoom' && selectedSlotType === 'day' ? [0] : [];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={he}>
      <Box sx={{ margin: '1.5rem 0', direction: 'rtl' }}>
        {canUseRecurring && (
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <ToggleButtonGroup
              value={dateMode}
              exclusive
              onChange={(_, value) => {
                if (value) onDateModeChange(value);
              }}
              aria-label="בחירת מצב תאריכים"
              size="small"
            >
              <ToggleButton value="single">תאריך בודד</ToggleButton>
              <ToggleButton value="recurring">סדרה חוזרת</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {dateMode === 'recurring' && canUseRecurring && (
          <Box sx={{ mb: 2 }}>
            <RecurringSeriesPicker
              minDate={minDate}
              disabledWeekdays={recurringDisabledWeekdays}
              shouldDisableDate={shouldDisableDate}
              onDatesChange={onSeriesDatesChange}
            />
          </Box>
        )}

        {isHour && dateMode === 'single' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            
            <Box>
              <Typography variant="body1" sx={{ mb: 1 }}>
                תאריך:
              </Typography>
              <DatePicker
                value={stringToDate(selectedDate)}
                onChange={(newValue) => {
                  const newDateString = dateToString(newValue);
                  onDateChange(newDateString);
                  // For hourly bookings, end date should always match the start date
                  onEndDateChange(newDateString);

                  if (!newValue) return;

                  const isNewDateToday = dateToString(newValue) === dateToString(new Date());
                  const isSundayMeetingRoom =
                    selectedType?.type === 'meetingRoom' && newValue.getDay() === 0;

                  const currentTimeRounded = new Date();
                  const mins = currentTimeRounded.getMinutes();
                  currentTimeRounded.setMinutes(Math.ceil(mins / 30) * 30, 0, 0);

                  const sundayMin = new Date();
                  sundayMin.setHours(14, 0, 0, 0);

                  // Clear start/end if below the effective min for this date
                  if (selectedStartTime) {
                    const startTime = stringToTime(selectedStartTime);
                    if (startTime) {
                      if (isNewDateToday && startTime < currentTimeRounded) {
                        onStartTimeChange('');
                      } else if (isSundayMeetingRoom && startTime < sundayMin) {
                        onStartTimeChange('');
                      }
                    }
                  }

                  if (selectedEndTime) {
                    const endTime = stringToTime(selectedEndTime);
                    if (endTime && isNewDateToday && endTime < currentTimeRounded) {
                      onEndTimeChange('');
                    }
                  }
                }}
                minDate={minDate}
                shouldDisableDate={shouldDisableDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined",
                    sx: { direction: 'ltr' }
                  }
                }}
              />
            </Box>
          </Box>
        )}

        {isHour && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Box>
              <Typography variant="body1" sx={{ mb: 1 }}>
                שעת התחלה:
                {sundayMeetingRoomMinTime && dateMode === 'single' && (
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                    ביום ראשון ניתן לבחור שעה מ-14:00 בלבד
                  </Typography>
                )}
                {dateMode === 'recurring' && (
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                    אותן שעות לכל המועדים בסדרה
                  </Typography>
                )}
              </Typography>
              <TimePicker
                value={stringToTime(selectedStartTime)}
                onChange={(newValue) => {
                  if (newValue) {
                    // Round to the nearest 30-minute interval
                    const roundedTime = new Date(newValue);
                    const minutes = roundedTime.getMinutes();
                    const roundedMinutes = Math.round(minutes / 30) * 30;
                    roundedTime.setMinutes(roundedMinutes, 0, 0);
                    onStartTimeChange(timeToString(roundedTime));
                    
                    // Auto-adjust end time if it becomes invalid with new start time
                    if (selectedEndTime) {
                      const currentEndTime = stringToTime(selectedEndTime);
                      if (currentEndTime) {
                        const durationHours = (currentEndTime.getTime() - roundedTime.getTime()) / (1000 * 60 * 60);
                        
                        // Check if current duration is valid
                        let needsAdjustment = false;
                        let newEndTime = currentEndTime;
                        
                        if (selectedType && selectedType.baseHours) {
                          const baseHours = selectedType.baseHours;
                          let isValidDuration = false;
                          
                          if (selectedType.extraHourPrice) {
                            // Tiered pricing: baseHours + any number of 1-hour increments
                            if (durationHours >= baseHours) {
                              const extraHours = durationHours - baseHours;
                              // Extra hours must be whole numbers (1-hour increments)
                              isValidDuration = Math.abs(extraHours % 1) < 0.001;
                            }
                          } else {
                            // Slot-based pricing: must be exact multiples of baseHours
                            isValidDuration = Math.abs(durationHours % baseHours) < 0.001 && durationHours >= Math.max(1, baseHours);
                          }
                          
                          if (!isValidDuration) {
                            needsAdjustment = true;
                            
                            if (selectedType.extraHourPrice) {
                              // For tiered pricing, round to nearest valid duration
                              if (durationHours < baseHours) {
                                // Less than base, set to base hours
                                newEndTime = new Date(roundedTime);
                                newEndTime.setHours(newEndTime.getHours() + baseHours);
                              } else {
                                // Round extra hours to nearest whole hour
                                const extraHours = Math.round(durationHours - baseHours);
                                newEndTime = new Date(roundedTime);
                                newEndTime.setHours(newEndTime.getHours() + baseHours + extraHours);
                              }
                            } else {
                              // For slot-based pricing, round to nearest slot multiple
                              const slotsNeeded = Math.max(1, Math.ceil(durationHours / baseHours));
                              newEndTime = new Date(roundedTime);
                              newEndTime.setHours(newEndTime.getHours() + (slotsNeeded * baseHours));
                            }
                          }
                        } else {
                          // For stations without baseHours, ensure minimum 1 hour
                          if (durationHours < 1) {
                            needsAdjustment = true;
                            newEndTime = new Date(roundedTime);
                            newEndTime.setHours(newEndTime.getHours() + 1);
                          }
                        }
                        
                        // Apply adjustment if needed and within business hours
                        if (needsAdjustment) {
                          if (newEndTime <= businessHoursEnd) {
                            onEndTimeChange(timeToString(newEndTime));
                          } else {
                            // Clear end time if it would exceed business hours
                            onEndTimeChange('');
                          }
                        }
                      }
                    }
                  } else {
                    onStartTimeChange('');
                  }
                }}
                minTime={getMinStartTime()}
                maxTime={businessHoursEnd}
                views={['hours', 'minutes']}
                format="HH:mm"
                minutesStep={30}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined",
                    sx: { direction: 'ltr' }
                  }
                }}
              />
            </Box>
            <Box>
              <Typography variant="body1" sx={{ mb: 1 }}>
                שעת סיום:
                {selectedType && selectedType.baseHours && selectedType.extraHourPrice && (
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                    מינימום {selectedType.baseHours} שעות, אחר כך בקפיצות של שעה
                  </Typography>
                )}
                {selectedType && selectedType.baseHours && !selectedType.extraHourPrice && (
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                    הזמנה בחבילות של {selectedType.baseHours} שעות בלבד
                  </Typography>
                )}
                {(!selectedType || !selectedType.baseHours) && (
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                    מינימום שעה אחת
                  </Typography>
                )}
              </Typography>
              <TimePicker
                value={stringToTime(selectedEndTime)}
                onChange={(newValue) => {
                  if (newValue && selectedStartTime) {
                    // Round to the nearest 30-minute interval
                    const roundedTime = new Date(newValue);
                    const minutes = roundedTime.getMinutes();
                    const roundedMinutes = Math.round(minutes / 30) * 30;
                    roundedTime.setMinutes(roundedMinutes, 0, 0);
                    
                    const startTime = stringToTime(selectedStartTime);
                    if (startTime) {
                      const durationHours = (roundedTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);
                      
                      // Check if station has baseHours constraint
                      if (selectedType && selectedType.baseHours) {
                        const baseHours = selectedType.baseHours;
                        let isValidDuration = false;
                        
                        if (selectedType.extraHourPrice) {
                          // Tiered pricing: baseHours + any number of 1-hour increments
                          if (durationHours >= baseHours) {
                            const extraHours = durationHours - baseHours;
                            // Extra hours must be whole numbers (1-hour increments)
                            isValidDuration = Math.abs(extraHours % 1) < 0.001;
                          }
                        } else {
                          // Slot-based pricing: must be exact multiples of baseHours
                          isValidDuration = Math.abs(durationHours % baseHours) < 0.001 && durationHours >= Math.max(1, baseHours);
                        }
                        
                        if (isValidDuration) {
                          onEndTimeChange(timeToString(roundedTime));
                        } else {
                          // Find the closest valid duration
                          let validEndTime;
                          
                          if (selectedType.extraHourPrice) {
                            // For tiered pricing, round to nearest valid duration
                            if (durationHours < baseHours) {
                              // Less than base, set to base hours
                              validEndTime = new Date(startTime);
                              validEndTime.setHours(validEndTime.getHours() + baseHours);
                            } else {
                              // Round extra hours to nearest whole hour
                              const extraHours = Math.round(durationHours - baseHours);
                              validEndTime = new Date(startTime);
                              validEndTime.setHours(validEndTime.getHours() + baseHours + extraHours);
                            }
                          } else {
                            // For slot-based pricing, round to nearest slot multiple
                            const slotsNeeded = Math.max(1, Math.round(durationHours / baseHours));
                            validEndTime = new Date(startTime);
                            validEndTime.setHours(validEndTime.getHours() + (slotsNeeded * baseHours));
                          }
                          
                          if (validEndTime <= businessHoursEnd) {
                            onEndTimeChange(timeToString(validEndTime));
                          }
                        }
                      } else {
                        // For stations without baseHours, just ensure minimum 1 hour
                        if (durationHours >= 1) {
                          onEndTimeChange(timeToString(roundedTime));
                        } else {
                          // Set to 1 hour minimum
                          const minEndTime = new Date(startTime);
                          minEndTime.setHours(minEndTime.getHours() + 1);
                          onEndTimeChange(timeToString(minEndTime));
                        }
                      }
                    }
                  } else {
                    onEndTimeChange('');
                  }
                }}
                minTime={(() => {
                  if (selectedStartTime) {
                    const startTime = stringToTime(selectedStartTime);
                    if (startTime) {
                      // Calculate minimum duration based on pricing model
                      let minDurationHours = 1; // Default minimum slot is 1 hour
                      if (selectedType && selectedType.baseHours) {
                        // For both tiered and slot-based pricing, minimum is baseHours
                        minDurationHours = Math.max(1, selectedType.baseHours);
                      }
                      
                      const minEndTime = new Date(startTime);
                      minEndTime.setHours(minEndTime.getHours() + minDurationHours);
                      
                      if (isToday) {
                        // If today, use the later of calculated min time, current time, or business hours start
                        const minTime = minEndTime > nowRounded ? minEndTime : nowRounded;
                        return minTime > businessHoursStart ? minTime : businessHoursStart;
                      }
                      return minEndTime > businessHoursStart ? minEndTime : businessHoursStart;
                    }
                  }
                  return isToday ? (nowRounded > businessHoursStart ? nowRounded : businessHoursStart) : businessHoursStart;
                })()}
                maxTime={businessHoursEnd}
                views={['hours', 'minutes']}
                format="HH:mm"
                minutesStep={30}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined",
                    sx: { direction: 'ltr' }
                  }
                }}
              />
            </Box>
          </Box>
        )}
        {isDayOrMonth && !isHour && dateMode === 'single' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="body1" sx={{ mb: 1 }}>
                תאריך התחלה:
              </Typography>
              <DatePicker
                value={stringToDate(selectedDate)}
                onChange={(newValue) => {
                  const newDateString = dateToString(newValue);
                  onDateChange(newDateString);

                  // Auto-calculate end date based on slot type
                  if (newValue) {
                    const endDate = new Date(newValue);
                    if (selectedSlotType === 'day') {
                      // For day type, end date is the same as start date
                      // endDate remains the same as newValue
                    } else if (selectedSlotType === 'month') {
                      endDate.setMonth(endDate.getMonth() + monthCount);
                    }
                    onEndDateChange(dateToString(endDate));
                  } else {
                    onEndDateChange('');
                  }
                }}
                minDate={minDate}
                shouldDisableDate={shouldDisableDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined",
                    sx: { direction: 'ltr' }
                  }
                }}
              />
            </Box>
            {isMonth && (
              <Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  מספר חודשים:
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                  }}
                >
                  <IconButton
                    aria-label="הפחת חודש"
                    onClick={() => onMonthCountChange(monthCount - 1)}
                    disabled={monthCount <= MIN_MONTH_COUNT}
                    color="primary"
                  >
                    <RemoveIcon />
                  </IconButton>
                  <Typography variant="h6" sx={{ minWidth: 32, textAlign: 'center' }}>
                    {monthCount}
                  </Typography>
                  <IconButton
                    aria-label="הוסף חודש"
                    onClick={() => onMonthCountChange(monthCount + 1)}
                    disabled={monthCount >= MAX_MONTH_COUNT}
                    color="primary"
                  >
                    <AddIcon />
                  </IconButton>
                </Box>
              </Box>
            )}
            <Box>
              <Typography variant="body1" sx={{ mb: 1 }}>
                תאריך סיום:
              </Typography>
              <DatePicker
                value={stringToDate(selectedEndDate)}
                onChange={() => {}} // No-op since it's read-only
                disabled={true}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined",
                    sx: { 
                      direction: 'ltr',
                      '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      }
                    }
                  }
                }}
              />
            </Box>
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default DateTimePicker;
